import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth'
import { useUpdateAppearance } from '@/hooks/useAppearances'
import { STAGES, type StageId } from '@/types'
import { GripVertical, ChevronRight, ChevronLeft } from 'lucide-react'
import { useLabels } from '@/lib/terminology'

interface GuestData {
  id: string
  name: string
  title: string
  company?: string | null
  email?: string | null
  avatar_url?: string | null
}

interface EpisodeData {
  id: string
  number: number
  title: string
  team_id: string
  recording_date: string
  air_date: string
}

interface AppearanceData {
  id: string
  stage: StageId
  ready_to_record: boolean
  declined: boolean
  guests: GuestData | null
  episodes: EpisodeData | null
}

export function PipelinePage() {
  const { membership } = useAuth()
  const supabase = createClient()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const updateAppearance = useUpdateAppearance()
  const labels = useLabels()
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const [dropTarget, setDropTarget] = useState<StageId | null>(null)
  const [movingId, setMovingId] = useState<string | null>(null)
  const [moveError, setMoveError] = useState<string | null>(null)

  const { data: appearances = [], isLoading } = useQuery({
    queryKey: ['pipeline', membership?.team_id],
    queryFn: async () => {
      if (!membership?.team_id) return []

      // First get episodes for this team
      const { data: episodes } = await supabase
        .from('episodes')
        .select('id')
        .eq('team_id', membership.team_id)

      if (!episodes || episodes.length === 0) return []

      const episodeIds = episodes.map((e: { id: string }) => e.id)

      const { data } = await supabase
        .from('appearances')
        .select(
          `
          *,
          guests(*),
          episodes(*)
        `
        )
        .in('episode_id', episodeIds)

      return (data as AppearanceData[]) || []
    },
    enabled: !!membership?.team_id,
  })

  const stageOrder: StageId[] = STAGES.map((stage) => stage.id)

  const handleMoveAppearance = (appearanceId: string, newStage: StageId) => {
    setMovingId(appearanceId)
    setMoveError(null)
    updateAppearance.mutate(
      { id: appearanceId, stage: newStage },
      {
        onSuccess: () => {
          setMovingId(null)
          queryClient.invalidateQueries({ queryKey: ['pipeline'] })
        },
        onError: () => {
          setMovingId(null)
          setMoveError(`Failed to move guest to ${newStage}. Please try again.`)
          setTimeout(() => setMoveError(null), 5000)
        },
      }
    )
  }

  const handleDragStart = (e: React.DragEvent, appearanceId: string) => {
    setDraggedId(appearanceId)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', appearanceId)
  }

  const handleDragOver = (e: React.DragEvent, stageId: StageId) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDropTarget(stageId)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDropTarget(null)
    }
  }

  const handleDrop = (e: React.DragEvent, stageId: StageId) => {
    e.preventDefault()
    const appearanceId = e.dataTransfer.getData('text/plain')
    if (appearanceId) {
      const appearance = appearances.find((a) => a.id === appearanceId)
      if (appearance && appearance.stage !== stageId) {
        handleMoveAppearance(appearanceId, stageId)
      }
    }
    setDraggedId(null)
    setDropTarget(null)
  }

  const handleDragEnd = () => {
    setDraggedId(null)
    setDropTarget(null)
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-zinc-200 dark:bg-zinc-800 rounded mb-2" />
          <div className="h-4 w-64 bg-zinc-200 dark:bg-zinc-800 rounded mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-zinc-100 dark:bg-zinc-800/50 rounded-xl p-4 h-96" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">{labels.pipeline}</h1>
      </div>

      {/* Error toast */}
      {moveError && (
        <div className="mb-4 px-4 py-3 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-lg flex items-center justify-between">
          <p className="text-sm text-rose-700 dark:text-rose-300">{moveError}</p>
          <button
            onClick={() => setMoveError(null)}
            className="text-rose-500 hover:text-rose-700 text-sm font-medium ml-4"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Pipeline Board */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {STAGES.map((stage) => {
          const stageAppearances = appearances.filter((a) => a.stage === stage.id)
          const isDropping = dropTarget === stage.id && draggedId !== null

          return (
            <div
              key={stage.id}
              className={`bg-zinc-100 dark:bg-zinc-800/50 rounded-xl p-4 min-h-[400px] transition-colors ${isDropping ? 'ring-2 ring-primary-500 bg-primary-50 dark:bg-primary-900/20' : ''}`}
              onDragOver={(e) => handleDragOver(e, stage.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, stage.id)}
              data-testid={`pipeline-stage-${stage.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-zinc-900 dark:text-zinc-100">{stage.label}</h3>
                <span className="text-sm text-zinc-500 bg-zinc-200 dark:bg-zinc-700 px-2 py-0.5 rounded-full">
                  {stageAppearances.length}
                </span>
              </div>

              <div className="space-y-3">
                {stageAppearances.length === 0 ? (
                  <p className="text-sm text-zinc-400 dark:text-zinc-500 text-center py-8">
                    No {labels.guests.toLowerCase()}
                  </p>
                ) : (
                  stageAppearances.map((appearance) => {
                    const guest = appearance.guests
                    const currentStageIndex = stageOrder.indexOf(appearance.stage)
                    const canMoveLeft = currentStageIndex > 0
                    const canMoveRight = currentStageIndex < stageOrder.length - 1
                    const isDragging = draggedId === appearance.id

                    return (
                      <div
                        key={appearance.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, appearance.id)}
                        onDragEnd={handleDragEnd}
                        className={`bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-zinc-200 dark:border-zinc-700 transition-opacity ${isDragging ? 'opacity-50' : ''}`}
                        data-testid={`pipeline-card-${appearance.id}`}
                      >
                        {/* Guest info - clicks to guest profile */}
                        <button
                          onClick={() => {
                            if (guest?.id) {
                              navigate(`/guests/${guest.id}`)
                            }
                          }}
                          className="w-full text-left p-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors cursor-pointer rounded-t-lg"
                        >
                          <div className="flex items-center gap-3">
                            <GripVertical aria-hidden="true" className="w-4 h-4 text-zinc-400 cursor-grab flex-shrink-0" />
                            <div className="w-10 h-10 rounded-full bg-secondary-500 flex items-center justify-center text-white text-sm font-medium flex-shrink-0 overflow-hidden">
                              {guest && guest.avatar_url ? (
                                <img
                                  src={guest.avatar_url}
                                  alt={guest.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                guest?.name?.charAt(0) || 'G'
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-zinc-900 dark:text-zinc-100 truncate">
                                {guest?.name || 'Guest'}
                              </p>
                              <p className="text-sm text-zinc-500 truncate">
                                {guest?.title || 'No title'}
                              </p>
                            </div>
                            {appearance.declined && (
                              <span className="text-xs px-1.5 py-0.5 bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 rounded">
                                Declined
                              </span>
                            )}
                            {appearance.ready_to_record && !appearance.declined && (
                              <span className="text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded">
                                Ready
                              </span>
                            )}
                          </div>
                        </button>
                        {/* Episode info - clicks to recording detail */}
                        {appearance.episodes && (
                          <button
                            onClick={() => {
                              if (appearance.episodes?.id) {
                                navigate(`/recordings/${appearance.episodes.id}`)
                              }
                            }}
                            className="w-full text-left px-3 py-2 border-t border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors cursor-pointer"
                          >
                            <p className="text-xs text-zinc-500 hover:text-primary-500 transition-colors">
                              Ep. {appearance.episodes.number}: {appearance.episodes.title}
                            </p>
                          </button>
                        )}
                        {/* Stage navigation arrows */}
                        <div className="flex items-center justify-between px-3 py-2 border-t border-zinc-100 dark:border-zinc-800">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              if (canMoveLeft) {
                                handleMoveAppearance(appearance.id, stageOrder[currentStageIndex - 1])
                              }
                            }}
                            disabled={!canMoveLeft || movingId === appearance.id}
                            aria-label={canMoveLeft ? `Move to ${stageOrder[currentStageIndex - 1]}` : 'Already at first stage'}
                            className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            data-testid={`move-left-${appearance.id}`}
                          >
                            <ChevronLeft className="w-4 h-4 text-zinc-500" />
                          </button>
                          <span className="text-[10px] text-zinc-400 uppercase tracking-wide">
                            {stage.label}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              if (canMoveRight) {
                                handleMoveAppearance(appearance.id, stageOrder[currentStageIndex + 1])
                              }
                            }}
                            disabled={!canMoveRight || movingId === appearance.id}
                            aria-label={canMoveRight ? `Move to ${stageOrder[currentStageIndex + 1]}` : 'Already at last stage'}
                            className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            data-testid={`move-right-${appearance.id}`}
                          >
                            <ChevronRight className="w-4 h-4 text-zinc-500" />
                          </button>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
